import { DEFAULT_FEATURE_FLAGS } from "~/modules/feature_flag/consts";
import { DEFAULT_EXPERIMENT_STATE } from "~/modules/experiments/consts";
import {
    OrganizationScope,
    PlanTypes,
    User,
    UserProfileAcquisitionChannels,
    UserProfileCompanyTypes,
    UserProfileOccupations,
    UserStoreState,
} from "./types";
import { FeatureNames } from "~/modules/feature_flag/flags";

// // // //

/**
 * EMPTY_USER_STATE
 * An empty User object used as a dummy to pass around when fetching the user
 * This primarily exists to simplify the type signature for the UserFetcher component
 */
export const EMPTY_USER_STATE: User = {
    id: "",
    name: "",
    email: "",
    emailVerified: undefined,
    image: "",
    role: "CUSTOMER",
    organization: {
        id: "",
        name: "",
        domain: "",
        scope: OrganizationScope.SINGLEUSER,
        autoEnroll: false,
        plan: {
            id: "",
            type: PlanTypes.FREE,
            organizationID: "",
            stripeCustomerID: null,
            stripePriceID: null,
            licensedSeats: 0,
            expiresAt: null,
        },
    },
    profile: {
        company: "",
        companyType: UserProfileCompanyTypes.None,
        occupation: UserProfileOccupations.None,
        acquisitionChannel: UserProfileAcquisitionChannels.None,
        assetClassInterests: [],
        shouldReceiveMarketing: false,
    },
    preferences: {
        darkMode: false,
        defaultOrganizationID: null,
    },
    memberships: [],
    pending_invitations: [],
    features: {
        ...DEFAULT_FEATURE_FLAGS,
    },
    experiments: {
        ...DEFAULT_EXPERIMENT_STATE,
    },
    intercom_hmac: null,
    createdAt: null,
};

/**
 * INITIAL_STATE
 * Defines the initial state for the User Redux store
 */
export const INITIAL_STATE: UserStoreState = {
    user: EMPTY_USER_STATE,
    isLoggedIn: false,
    loading: true,
    error: null,
};

// // // //

/**
 * EMPTY_USER_STATE
 * An empty User object used as a dummy to pass around when fetching the user
 * This primarily exists to simplify the type signature for the UserFetcher component
 */
export const DUMMY_USER_STATE: User = {
    id: "-12345",
    name: "John Doe",
    email: "john@example.com",
    emailVerified: new Date().toISOString(),
    image: "",
    role: "SUPERADMIN",
    organization: {
        id: "-12345",
        name: "devin-doe-org",
        domain: "devin.com",
        scope: OrganizationScope.SINGLEUSER,
        autoEnroll: false,
        plan: {
            id: "12345",
            type: PlanTypes.FREE,
            organizationID: "-12345",
            stripeCustomerID: null,
            stripePriceID: null,
            licensedSeats: 0,
            expiresAt: null,
        },
    },
    profile: {
        company: "Test Company",
        companyType: UserProfileCompanyTypes.InfrastructureProvider,
        occupation: UserProfileOccupations.ApplicationDeveloper,
        acquisitionChannel: UserProfileAcquisitionChannels.WordOfMouth,
        assetClassInterests: [],
        shouldReceiveMarketing: false,
    },
    preferences: {
        darkMode: false,
    },
    memberships: [
        {
            id: "09876",
            role: "OWNER",
            organizationID: "-12345",
            userID: "-12345",
            invitedName: null,
            invitedEmail: null,
            organization: {
                id: "-12345",
                name: "devin-doe-org",
                domain: "devin.com",
                scope: OrganizationScope.SINGLEUSER,
                autoEnroll: false,
                plan: {
                    id: "12345",
                    type: PlanTypes.FREE,
                    organizationID: "-12345",
                    stripeCustomerID: null,
                    stripePriceID: null,
                    licensedSeats: 0,
                    expiresAt: null,
                },
            },
        },
        {
            id: "98765",
            role: "OWNER",
            organizationID: "-98765",
            userID: "-12345",
            invitedName: null,
            invitedEmail: null,
            organization: {
                id: "-98765",
                name: "devin-pro",
                domain: "devin-pro.com",
                scope: OrganizationScope.SINGLEUSER,
                autoEnroll: false,
                plan: {
                    id: "23456",
                    type: PlanTypes.PREMIUM_ALL_ACCESS,
                    organizationID: "-98765",
                    stripeCustomerID: null,
                    stripePriceID: null,
                    licensedSeats: 1,
                    expiresAt: null,
                },
            },
        },
    ],
    pending_invitations: [],
    features: {
        ...DEFAULT_FEATURE_FLAGS,
        [FeatureNames.enableDataDownloads]: true,
        [FeatureNames.enableApiKeys]: true,
        [FeatureNames.enableMcpTools]: true,
        [FeatureNames.enableAdminTools]: true,
        [FeatureNames.enableExperimentalFeatures]: true,
    },
    experiments: {
        ...DEFAULT_EXPERIMENT_STATE,
    },
    intercom_hmac: null,
    createdAt: null,
};
